import type { AiStrategyBundle } from "@/types/improvement";
import type { MarketplaceKey } from "@/marketplaces/registry";
import type { WbGood } from "@/api/parseWb/parseWbGood";
import type { AIVariant } from "@/api/mockAI";
import type { WbCharacteristic } from "@/api/wbApi/getGoodCharacteristics";
import type { TimeRange } from "@/entities/goodVersion";
export type GoodAddingWizardStep = "editGoodInfoManually" | "editGoodWithAI" | "viewGoodVersion" | "viewGoodVersionsList";
export type ImprovePhase = "pipeline" | "editor";

export type WbCategoryData = { name: string; rootName?: string; id: number; rootId?: number };
export type CategoryData = { wb?: WbCategoryData };
import type { VariantItem } from "@/components/createGood/editGoodWithAI/PhotoSection.vue";

const STORAGE_KEY = "cg:content-creation-wizard";
const SCHEMA_VERSION = 1;

export type PendingPhotoQuickEdit = {
  targetImageId: string;
  sourcePhotoId: string;
  editType: string;
  baseUrl: string;
  slot: "duplicate" | "in-place";
  /** Для «Свой промпт» в полноэкранном режиме / «Своя идея». */
  customPrompt?: string;
  /** Stable Idempotency-Key reused on replay after reload — prevents duplicate GENI spend. */
  idempotencyKey?: string;
};

export type PendingTextTask =
  | {
      taskId: string;
      mode: "variant";
      field: "title" | "description";
      strategyIndex: number;
    }
  | {
      taskId: string;
      mode: "refine";
      field: "title" | "description";
      userText: string;
      strategyIndex: number;
    };

export type PersistedWizardImage = {
  id: string;
  url: string;
  localPhotoId?: string;
  variants: VariantItem[];
  pickedVariantIndex?: number;
  selectedVariantId?: string;
  generating?: boolean;
  /**
   * GC-21+GC-36: AI-слот, которым владеет content-pipeline (фикс GC-36
   * создаёт такие слоты после оригиналов). У них url совпадает с предыдущим
   * слотом-оригиналом (placeholder), поэтому reconcileOrphanedPhotoQuickEdits
   * иначе ошибочно опознаёт их как «duplicate slot» и запускает enhanceQuality
   * после F5, повторно списывая geni.
   */
  pipelineSlot?: boolean;
};

export type PersistedWizardSnapshot = {
  version: typeof SCHEMA_VERSION;
  savedAt: number;
  userId: string;
  wizardSessionId: string;
  step: GoodAddingWizardStep;
  history: GoodAddingWizardStep[];
  improvePhase: ImprovePhase;
  manualStepBackLocked: boolean;
  preparationProgress: number;
  preparationStageIndex: number;
  strategyBundles: AiStrategyBundle[];
  selectedStrategyIndex: number;
  deepAnalysisCacheKey: string;
  lastContentPipelineSucceeded: boolean;
  waitingForGoodsSync: boolean;
  pipelineInFlight: boolean;
  pipelineSourceCatalog: { id: string; url: string }[];
  activeContentJob: { jobId: string; ownerId: string } | null;
  /** Быстрые правки фото на шаге 2 — клиентская генерация без content-job. */
  pendingPhotoQuickEdits: PendingPhotoQuickEdit[];
  /** Перегенерация заголовка/описания или правка текста по идее в чате. */
  pendingTextTasks: PendingTextTask[];
  /** Очередь фото для «Перегенерировать все» (in-place). */
  pendingRegenerateAllPhotoIds: string[];
  /** Выбранные фото при импорте по URL (шаг 1). */
  manualStepUrlSelectedIds?: string[];
  /** ИИ-анализ фото прерван reload — возобновить на шаге 1. */
  pendingPhotoAnalysis?: { sourceImageId: string } | null;
  /** Импорт по URL прерван reload — повторить загрузку. */
  pendingUrlImport?: { url: string } | null;
  /** Черновик URL в поле ввода (до/после «Загрузить товар»). */
  manualStepImportUrl?: string;
  data: {
    marketplace: MarketplaceKey | null;
    method: "import" | "manual" | null;
    contentType: "photo" | "card" | "video" | null;
    productSourceType: "url" | "image" | null;
    generationNotes: string;
    generalCategory: string;
    product: {
      goodId: string;
      versionId: string;
      title: string;
      description: string;
      price: number | null;
      images: PersistedWizardImage[];
      videoUrl: string | null;
      characteristics: { name: string; value: string }[];
      aiTitles: AIVariant[];
      aiDescriptions: AIVariant[];
      selectedAiTitleId: string | null;
      selectedAiDescriptionId: string | null;
      generatingTitle: boolean;
      generatingDescription: boolean;
      competitors: WbGood[];
      savingVersion: boolean;
      isActive: boolean;
      category: CategoryData | null;
      brand: string | null;
      characteristicsSchema: WbCharacteristic[] | null;
      activePeriods: TimeRange[];
      originalProductTitle: string | null;
      originalProductDescription: string | null;
    };
  };
};

function isTransientImageUrl(url: string | undefined): boolean {
  if (!url) return false;
  return url.startsWith("blob:") || url.startsWith("data:");
}

export function serializeWizardImage(image: {
  id: string;
  url: string;
  variants: VariantItem[];
  pickedVariantIndex?: number;
  selectedVariantId?: string;
  generating?: boolean;
  pipelineSlot?: boolean;
}): PersistedWizardImage {
  const transient = isTransientImageUrl(image.url);
  return {
    id: image.id,
    url: transient ? "" : image.url,
    localPhotoId: transient ? image.id : undefined,
    variants: image.variants.map((variant) => ({
      ...variant,
      url: isTransientImageUrl(variant.url) ? "" : variant.url,
    })),
    pickedVariantIndex: image.pickedVariantIndex,
    selectedVariantId: image.selectedVariantId,
    generating: image.generating,
    pipelineSlot: image.pipelineSlot,
  };
}

export function readContentCreationWizardSnapshot(): PersistedWizardSnapshot | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedWizardSnapshot;
    if (!parsed || parsed.version !== SCHEMA_VERSION || !parsed.userId || !parsed.wizardSessionId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeContentCreationWizardSnapshot(snapshot: PersistedWizardSnapshot): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch (error) {
    console.error("[contentCreationWizardPersistence] failed to write snapshot", error);
  }
}

export function clearContentCreationWizardSnapshot(): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(STORAGE_KEY);
}

function hasCompletedEnhanceVariant(img: PersistedWizardImage): boolean {
  return (img.variants ?? []).some(
    (variant) => variant.source === "ai:enhanceQuality" && Boolean(String(variant.url ?? "").trim()),
  );
}

function isLikelyQuickEditDuplicateSlot(img: PersistedWizardImage, prev: PersistedWizardImage | null): boolean {
  if (!prev || prev.id === img.id) return false;
  if (prev.url !== img.url) return false;
  return !hasCompletedEnhanceVariant(img);
}

/**
 * После reload дубликат фото мог остаться в snapshot без `pendingPhotoQuickEdits`
 * (гонка debounced persist / pagehide / clearPending в finally при ошибке).
 * Восстанавливаем очередь по `generating` или по «копии» слота справа без AI-варианта.
 */
/**
 * Stable, deterministic Idempotency-Key for a photo quick-edit, derived ONLY from
 * the target slot id (which is persisted and survives a page refresh). Using the
 * same formula everywhere — including when an orphaned slot is reconstructed after
 * reload — guarantees the same key is sent, so the backend charges geni exactly once
 * even if the original pending edit (with its key) was lost (BUG-001/002).
 */
export function quickEditIdempotencyKey(targetImageId: string): string {
  return `quickedit:${targetImageId}`;
}

export function reconcileOrphanedPhotoQuickEdits(
  persistedImages: PersistedWizardImage[],
  existingPending: PendingPhotoQuickEdit[],
): PendingPhotoQuickEdit[] {
  const pending = existingPending.map((edit) => ({ ...edit }));
  const pendingTargets = new Set(pending.map((edit) => edit.targetImageId));

  for (let i = 0; i < persistedImages.length; i++) {
    const img = persistedImages[i];
    if (pendingTargets.has(img.id)) continue;
    if (hasCompletedEnhanceVariant(img)) continue;
    // GC-21+GC-36: пропускаем слоты, которыми владеет content-pipeline.
    // Их url совпадает с url предыдущего слота-оригинала (это placeholder, пока
    // не пришёл AI-результат) — иначе reconcile принял бы их за «duplicate slot
    // pattern» и запустил enhanceQuality после каждого F5, повторно списывая geni.
    if (img.pipelineSlot) continue;

    const prev = i > 0 ? persistedImages[i - 1] : null;
    const isDuplicateSlot = isLikelyQuickEditDuplicateSlot(img, prev);
    // Only resurrect slots that are EXPLICITLY mid-generation. Relying on the
    // URL-duplicate heuristic alone caused a re-uploaded *identical* photo (same URL
    // as its neighbour) to be mistaken for an unfinished quick-edit and charged on
    // refresh. A genuine in-flight quick-edit slot is persisted with generating:true,
    // so this still recovers it — without false-charging duplicate uploads.
    if (!img.generating) continue;
    if ((img.variants?.length ?? 0) > 0 && !isDuplicateSlot) continue;

    const baseUrl = String(img.url || prev?.url || "").trim();
    if (!baseUrl) continue;

    let sourcePhotoId = img.id;
    let slot: PendingPhotoQuickEdit["slot"] = "in-place";
    if (isDuplicateSlot && prev) {
      sourcePhotoId = prev.id;
      slot = "duplicate";
    }

    pending.push({
      targetImageId: img.id,
      sourcePhotoId,
      editType: "enhanceQuality",
      baseUrl,
      slot,
      // Must equal the key used by the ORIGINAL edit (runProductPhotoQuickEdit and the
      // chat edit both call quickEditIdempotencyKey), so the original spend and any
      // reconcile-resume after F5 collapse to exactly ONE charge. Supersedes GC-21's
      // `reconcile:enhanceQuality:${img.id}`, which would not match the original key.
      idempotencyKey: quickEditIdempotencyKey(img.id),
    });
    pendingTargets.add(img.id);
  }

  return pending;
}

export function hasStep1WizardContent(data: PersistedWizardSnapshot["data"]): boolean {
  const product = data.product;
  return (
    data.contentType != null ||
    product.images.length > 0 ||
    Boolean(product.title?.trim()) ||
    Boolean(product.description?.trim()) ||
    Boolean(data.generationNotes?.trim()) ||
    Boolean(data.generalCategory?.trim()) ||
    Boolean(product.brand?.trim()) ||
    data.method != null ||
    data.productSourceType != null
  );
}

/** Есть ли смысл восстанавливать snapshot после reload. */
export function isRestorableWizardSnapshot(snapshot: PersistedWizardSnapshot): boolean {
  if (snapshot.step !== "editGoodInfoManually") return true;
  return (
    hasStep1WizardContent(snapshot.data) ||
    Boolean(snapshot.pendingUrlImport?.url.trim()) ||
    Boolean(snapshot.pendingPhotoAnalysis?.sourceImageId)
  );
}

/** Заполнены ли ключевые поля после ИИ-анализа загруженного фото. */
export function isManualStepPhotoAnalysisComplete(data: PersistedWizardSnapshot["data"]): boolean {
  const product = data.product;
  const hasTitle = Boolean(product.title?.trim());
  const hasDescription = Boolean(product.description?.trim());
  const hasCategory =
    Boolean(data.generalCategory?.trim()) || Boolean(product.category?.wb?.name?.trim());
  const hasCharacteristics = product.characteristics.length > 0;
  return hasTitle && hasDescription && (hasCategory || hasCharacteristics);
}

export function hasContentCreationWizardSnapshotForUser(userId: string): boolean {
  const snapshot = readContentCreationWizardSnapshot();
  return Boolean(snapshot && snapshot.userId === userId && isRestorableWizardSnapshot(snapshot));
}
